import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Users, TrendingUp, ShoppingBag,
  DollarSign, Brain, ChevronLeft, ChevronRight, MapPin, FileDown,
  BarChart3, Wallet, PieChart, LineChart, Megaphone, Building2,
  Factory, FileSpreadsheet, ShoppingCart, Shield, LogOut
} from "lucide-react";
import { useState } from "react";
import DateRangeFilter from "@/components/DateRangeFilter";

const cohortItems = [
  { path: "/cohort/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/cohort/analysis", label: "Cohort & Retention", icon: Users },
  { path: "/cohort/ltv", label: "LTV Analysis", icon: TrendingUp },
  { path: "/cohort/category", label: "Category Analysis", icon: ShoppingBag },
  { path: "/cohort/revenue", label: "Revenue Expansion", icon: DollarSign },
  { path: "/cohort/geography", label: "Geography", icon: MapPin },
  { path: "/cohort/ads", label: "Ads, Traffic & CAC", icon: Megaphone },
  { path: "/cohort/export", label: "Export Report", icon: FileDown },
];

const financialItems = [
  { path: "/financial/pnl", label: "P&L Statement", icon: BarChart3 },
  { path: "/financial/channels", label: "Channel P&L", icon: Wallet },
  { path: "/financial/revenue", label: "Revenue Breakdown", icon: PieChart },
  { path: "/financial/projections", label: "Projections", icon: LineChart },
  { path: "/financial/far", label: "Fixed Assets", icon: Factory },
  { path: "/financial/sales", label: "Sales Analysis", icon: ShoppingCart },
  { path: "/financial/export", label: "Export Report", icon: FileSpreadsheet },
];

export default function Layout({ user, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);

  const perms = user?.permissions || [];
  const hasCohort = perms.includes("cohort") || perms.includes("admin");
  const hasFinancial = perms.includes("financial") || perms.includes("admin");
  const isAdmin = user?.role === "super_admin";

  const navSections = [
    { label: "Company", items: [{ path: "/", label: "Overview", icon: Building2 }] },
  ];
  if (hasCohort) navSections.push({ label: "Cohort Analysis", items: cohortItems });
  if (hasFinancial) navSections.push({ label: "Financial Model", items: financialItems });
  navSections.push({ label: "Tools", items: [{ path: "/insights", label: "AI Chat", icon: Brain }] });
  if (isAdmin) navSections.push({ label: "Admin", items: [{ path: "/admin/users", label: "User Management", icon: Shield }] });

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      <aside className={`${collapsed ? "w-14" : "w-56"} flex-shrink-0 border-r border-slate-800/60 bg-[#070c18] flex flex-col transition-all duration-300`} data-testid="sidebar">
        <div className={`flex items-center ${collapsed ? "justify-center" : "px-4"} h-14 border-b border-slate-800/60`}>
          {!collapsed ? (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded bg-blue-600 flex items-center justify-center"><TrendingUp className="w-3.5 h-3.5 text-white" /></div>
              <div>
                <h1 className="font-space text-xs font-semibold text-slate-100 tracking-tight leading-none">StarAndDaisy</h1>
                <p className="text-[9px] text-slate-500 tracking-wider uppercase mt-0.5">Investor Connect</p>
              </div>
            </div>
          ) : (
            <div className="w-7 h-7 rounded bg-blue-600 flex items-center justify-center"><TrendingUp className="w-3.5 h-3.5 text-white" /></div>
          )}
        </div>

        <nav className="flex-1 py-2 overflow-y-auto" data-testid="nav-menu">
          {navSections.map((section) => (
            <div key={section.label} className="mb-1">
              {!collapsed && <div className="px-4 py-1.5"><span className="text-[8px] uppercase tracking-[0.15em] text-slate-600 font-semibold">{section.label}</span></div>}
              {section.items.map((item) => (
                <NavLink key={item.path} to={item.path} end={item.path === "/"}
                  data-testid={`nav-${item.path.replace(/\//g, "-").replace(/^-/, "") || "home"}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 ${collapsed ? "justify-center px-2" : "px-4"} py-[7px] text-[12px] transition-all duration-200 ${
                      isActive ? "nav-link-active text-blue-400 font-medium" : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40"
                    }`
                  }>
                  <item.icon className="w-[14px] h-[14px] flex-shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* User footer */}
        <div className={`border-t border-slate-800/60 ${collapsed ? "p-2" : "px-3 py-2"}`}>
          {!collapsed && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-semibold text-slate-300">{user?.username?.[0]?.toUpperCase() || "U"}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] text-slate-300 truncate">{user?.username}</p>
                  <p className="text-[9px] text-slate-600">{isAdmin ? "Super Admin" : "User"}</p>
                </div>
              </div>
              <button onClick={onLogout} className="text-slate-500 hover:text-red-400 transition-colors p-1" data-testid="logout-btn" title="Logout">
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {collapsed && (
            <button onClick={onLogout} className="w-full flex justify-center text-slate-500 hover:text-red-400 transition-colors" data-testid="logout-btn-collapsed">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto" data-testid="main-content">
        <div className="hero-glow min-h-full">
          <div className="sticky top-0 z-30 backdrop-blur-md bg-[#020617]/80 border-b border-slate-800/40 px-6 lg:px-8 py-2 flex items-center justify-end">
            <DateRangeFilter />
          </div>
          <div className="max-w-[1440px] mx-auto p-5 lg:p-7"><Outlet /></div>
        </div>
      </main>
    </div>
  );
}
